import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useCustomer } from "../context/CustomerContext";
import styles from "./AuthCustomerModal.module.css";
import ForgotPasswordModal from "./ForgotPasswordModal";
import GoogleCustomerAuthButton from "./GoogleCustomerAuthButton";
import { redirectAfterCustomerAuth } from "../utils/customerAuthRedirect";

const AuthCustomerModal = ({ onClose, onRegisterClick }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { login, loginWithGoogle } = useCustomer();
  const navigate = useNavigate();

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(form.email, form.password);

      if (onClose) onClose();
      redirectAfterCustomerAuth(navigate);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Помилка входу. Перевірте email і пароль."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setError("");
    setLoading(true);

    try {
      const data = await loginWithGoogle(credential);
      if (onClose) onClose();
      redirectAfterCustomerAuth(navigate, {
        isNewCustomer: Boolean(data?.isNewCustomer),
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Не вдалося увійти через Google. Спробуйте ще раз."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (message) => {
    setError(message || "Google авторизацію скасовано або не завершено.");
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const modalContent = (
    <>
      {!showForgotPassword && (
        <div className={styles.backdrop} onClick={handleBackdrop}>
          <div className={styles.modalWindow}>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Закрити"
            >
              ×
            </button>
            <h3 className={styles.formTitle}>Вхід в особистий кабінет</h3>
            <form
              className={styles.form}
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <div className={styles.field}>
                <label htmlFor="loginEmail">Email</label>
                <input
                  className={styles.input}
                  id="loginEmail"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  disabled={loading}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="loginPassword">Пароль</label>
                <input
                  className={styles.input}
                  id="loginPassword"
                  name="password"
                  type="password"
                  required
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  disabled={loading}
                />
              </div>
              {error && <div className={styles.error}>{error}</div>}
              <button
                className={styles.submit}
                type="submit"
                disabled={loading}
              >
                {loading ? "Вхід..." : "Увійти"}
              </button>
            </form>
            <GoogleCustomerAuthButton
              label="Увійти через Google"
              disabled={loading}
              onCredential={handleGoogleCredential}
              onError={handleGoogleError}
            />
            <div className={styles.switchLine}>
              Ще не маєте акаунту?
              <span className={styles.link} onClick={onRegisterClick}>
                Реєстрація
              </span>
            </div>
            <div className={styles.switchLine}>
              <span
                className={styles.link}
                onClick={() => setShowForgotPassword(true)}
              >
                Забули пароль?
              </span>
            </div>
          </div>
        </div>
      )}

      {showForgotPassword && (
        <ForgotPasswordModal
          onClose={onClose}
          onBackToLogin={() => setShowForgotPassword(false)}
        />
      )}
    </>
  );

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(modalContent, document.body);
};

export default AuthCustomerModal;
